import {
  faArrowLeft as ArrowLeftIcon,
  faPen as EditIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import ExportButton from '@/components/ExportButton';
import MultiPartScoreBadge from '@/components/MultiPartScoreBadge/MultiPartScoreBadge';
import PERMISSIONS from '@/constants/auth.constants';
import {
  assetRiskScoreDialogAtom,
  assetsFiltersAtom,
  assetsTimeFilterAtom,
  scorePartsNoTotal,
  selectedAssetAtom,
  useFetchAssetAlerts,
  useFetchAssetAlertsPerDay,
  useFetchAssetVulnerabilities
} from '@/pages/Assets';
import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import isLayoutMobileAtom from '@/store/layout';

import AssetActionsButton from '../AssetActionsButton';
import { AssetAddEditDialog } from '../AssetAddEditDialog';
import { parseSearchFiltersToQueryParams } from '../SearchBar';
import useStyles from './AssetSubTopbarTop.styles';

const AssetSubTopbarTop = () => {
  const isLayoutMobile = useAtomValue(isLayoutMobileAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
  const selectedAsset = useAtomValue(selectedAssetAtom);
  const organizations = useAtomValue(organizationsAtom);
  const [, setAssetRiskScoreDialog] = useAtom(assetRiskScoreDialogAtom);
  const navigate = useNavigate();
  const [assetTimeFilter] = useAtom(assetsTimeFilterAtom);
  const [assetsFilters] = useAtom(assetsFiltersAtom);
  const { data: assetAlerts } = useFetchAssetAlerts();
  const { data: assetAlertsPerDay } = useFetchAssetAlertsPerDay();
  const { data: vulnerabilities } = useFetchAssetVulnerabilities(
    selectedAsset?.id || 0
  );

  const organization =
    organizations.find((org) => org.id === selectedAsset?.organization_id) ||
    selectedOrganization;

  const orgHasVMPE = organization.product_type.includes('VMPE');

  const { classes } = useStyles({
    isLayoutMobile
  });

  const handleBack = () => {
    navigate({
      pathname: '/asset-inventory',
      search: createSearchParams({
        t: assetTimeFilter,
        q: parseSearchFiltersToQueryParams(assetsFilters)
      }).toString()
    });
  };

  const handleEditAsset = () => {
    // Logic to handle asset editing
    setOpenAddEditDialog(true);
  };

  const downloadAlertsPerDay = useMemo(() => {
    return (
      assetAlertsPerDay?.map(
        ({ organization_id: _organization_id, ...rest }) => rest
      ) || []
    );
  }, [assetAlertsPerDay]);

  const downloadVulnerabilities = useMemo(() => {
    return (
      vulnerabilities?.map(({ vmp_exposure_index, scanner_type, ...rest }) => ({
        ...rest,
        exposure_index: vmp_exposure_index,
        type: scanner_type
      })) || []
    );
  }, [vulnerabilities]);

  const assetDataSets = useMemo(() => {
    return [
      {
        name: 'Asset Alerts',
        data: assetAlerts || []
      },
      {
        name: 'Asset Alerts Per Day',
        data: orgHasVMPE ? downloadAlertsPerDay || [] : assetAlertsPerDay || []
      },
      {
        name: 'Asset Details',
        data: selectedAsset ? [selectedAsset] : []
      },
      ...(orgHasVMPE
        ? [
            {
              name: 'Vulnerabilities',
              data: downloadVulnerabilities || []
            }
          ]
        : [])
    ];
  }, [assetAlerts, assetAlertsPerDay, vulnerabilities]);

  return (
    <div
      className={classNames(
        'assetDetailsSubTopbar',
        classes.assetDetailsSubTopbar
      )}
    >
      <div className={classes.topContent}>
        <Stack direction="row" sx={{ alignItems: 'start' }} spacing={1}>
          <MultiPartScoreBadge
            score={selectedAsset?.risk_score || 0}
            parts={scorePartsNoTotal(selectedAsset?.risk_score_details || {})}
            height={50}
            width={50}
            handleClick={() => setAssetRiskScoreDialog(selectedAsset)}
          />
          <div>
            <Typography
              variant="h6"
              style={{ display: 'inline', fontWeight: 'normal' }}
            >
              Asset
              {selectedOrganization.id !== organization?.id && (
                <>
                  {' '}
                  for{' '}
                  <Typography
                    className={classes.topContentOrganizationName}
                    sx={{ color: 'warning.dark' }}
                  >
                    {organization?.name}
                  </Typography>
                </>
              )}
            </Typography>
            <Typography component="h1" variant="h3">
              {selectedAsset?.asset_name}
            </Typography>
          </div>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          {isLayoutMobile && (
            <Button color="primary" variant="outlined" onClick={handleBack}>
              <FontAwesomeIcon icon={ArrowLeftIcon} size="xl" />
            </Button>
          )}
          <ExportButton
            datasets={assetDataSets}
            fileName={selectedAsset?.asset_name || 'Asset Details'}
            json
          />
          <CheckPermissions permissions={[PERMISSIONS.ASSET_UPDATE]}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon={EditIcon} size="xs" />}
              sx={{ whiteSpace: 'nowrap' }}
              {...(isLayoutMobile && { fullWidth: true })}
              onClick={handleEditAsset}
            >
              Edit Asset
            </Button>
            <AssetActionsButton showEdit={false} />
          </CheckPermissions>
        </Stack>
      </div>
      {openAddEditDialog && (
        <AssetAddEditDialog
          open={openAddEditDialog}
          onClose={() => setOpenAddEditDialog(false)}
          type="edit"
        />
      )}
    </div>
  );
};

export default AssetSubTopbarTop;
